import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { act, read } from "./api";

export default function SegmentEnrollment({
  api,
  segments,
  workflowId,
  versionId,
  onClose,
}) {
  const [workflows, setWorkflows] = useState([]);
  const [chosen, setChosen] = useState(workflowId || "");
  const [version, setVersion] = useState(versionId || "");
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let active = true;
    read(api, "workflows")
      .then((items) => active && setWorkflows(items))
      .catch((err) => active && setError(err.message));
    return () => {
      active = false;
    };
  }, [api]);
  const workflow = workflows.find((item) => item.id === chosen);
  const versions = [...(workflow?.versions || [])].sort((a, b) =>
    a.id === workflow.defaultVersionId
      ? -1
      : b.id === workflow.defaultVersionId
        ? 1
        : b.number - a.number,
  );
  const payload = {
    workflowId: chosen,
    versionId: version,
    segmentIds: segments.map((item) => item.id),
  };
  const action = async (launch) => {
    setBusy(true);
    try {
      const result = await act(
        api,
        launch ? "enrollSegments" : "previewEnrollment",
        payload,
      );
      if (launch) {
        enqueueSnackbar(
          `Segment enrollment queued (operation ${result.operationId}).`,
          { variant: "success" },
        );
        onClose();
      } else setPreview(result);
    } catch (err) {
      enqueueSnackbar(err.message, { variant: "error" });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Enroll segments to workflow</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            select
            label="Workflow"
            value={chosen}
            onChange={(event) => {
              const id = event.target.value;
              setChosen(id);
              const item = workflows.find((row) => row.id === id);
              setVersion(item.defaultVersionId || item.versions[0]?.id || "");
              setPreview(null);
            }}
          >
            {workflows
              .filter(
                (row) =>
                  row.enabled &&
                  row.versions.length &&
                  segments.every((segment) => segment.kind === row.kind),
              )
              .map((row) => (
                <MenuItem key={row.id} value={row.id}>
                  {row.name}
                </MenuItem>
              ))}
          </TextField>
          <TextField
            select
            label="Live version"
            value={version}
            onChange={(event) => {
              setVersion(event.target.value);
              setPreview(null);
            }}
          >
            {versions.map((row) => (
              <MenuItem key={row.id} value={row.id}>
                Version {row.number}
                {row.id === workflow.defaultVersionId ? " (default)" : ""}
              </MenuItem>
            ))}
          </TextField>
          <Typography>
            All current segment members are considered. Only eligible recipients
            are enrolled. Later membership changes do not automatically enroll
            anyone.
          </Typography>
          {preview && (
            <Typography>
              {preview.eligible} eligible; {preview.excluded} excluded;{" "}
              {preview.already_enrolled} already enrolled; {preview.ineligible}{" "}
              otherwise ineligible.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button disabled={busy || !version} onClick={() => action(false)}>
          Preview
        </Button>
        <Button
          variant="contained"
          disabled={busy || !preview?.eligible}
          onClick={() => action(true)}
        >
          Confirm enrollment
        </Button>
      </DialogActions>
    </Dialog>
  );
}
